import { useEffect, useState, useCallback, useRef } from 'react';
import type { TooltipData } from './EngineStateTooltip';
import type { TypedHighcharts } from '../../InvestigationTabPane';
import { MARKER_CLASS, TOOLTIP_ATTR } from './constants';

interface TooltipState {
  data: TooltipData | null;
  position: { x: number; y: number } | null;
  visible: boolean;
}

interface UseEngineStateTooltipOptions {
  containerRef: React.RefObject<HTMLElement>;
  enabled: boolean;
  chart?: TypedHighcharts | null;
}

interface UseEngineStateTooltipReturn {
  tooltipState: TooltipState;
}

const findMarker = (target: EventTarget | null): HTMLElement | null => {
  if (!target || !(target instanceof Element)) return null;

  return target.closest<HTMLElement>(`.${MARKER_CLASS}`);
};

const parseTooltipData = (encodedData: string): TooltipData | null => {
  try {
    const json = decodeURIComponent(atob(encodedData));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const calculatePosition = (mouseX: number, mouseY: number) => {
  return { x: mouseX, y: mouseY };
};

export const useEngineStateTooltip = ({
  containerRef,
  enabled,
  chart,
}: UseEngineStateTooltipOptions): UseEngineStateTooltipReturn => {
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    data: null,
    position: null,
    visible: false,
  });

  const isHoveringMarkerRef = useRef(false);

  const disableChartTooltip = useCallback(() => {
    if (chart?.tooltip) {
      chart.tooltip.hide();
      chart.tooltip.options.enabled = false;
    }
  }, [chart]);

  const enableChartTooltip = useCallback(() => {
    if (chart?.tooltip) {
      chart.tooltip.options.enabled = true;
    }
  }, [chart]);

  const showTooltip = useCallback(
    (data: TooltipData, x: number, y: number) => {
      if (!isHoveringMarkerRef.current) {
        isHoveringMarkerRef.current = true;
        disableChartTooltip();
      }
      setTooltipState({
        data,
        position: calculatePosition(x, y),
        visible: true,
      });
    },
    [disableChartTooltip],
  );

  const hideTooltip = useCallback(() => {
    if (isHoveringMarkerRef.current) {
      isHoveringMarkerRef.current = false;
      enableChartTooltip();
    }
    setTooltipState(prev => ({ ...prev, visible: false }));
  }, [enableChartTooltip]);

  const handleMouseOver = useCallback(
    (e: MouseEvent) => {
      const marker = findMarker(e.target);
      if (!marker) return;

      const attr = marker.getAttribute(TOOLTIP_ATTR);
      if (!attr) return;

      const data = parseTooltipData(attr);
      if (!data || !data.status || !data.date) return;

      showTooltip(data, e.clientX, e.clientY);
    },
    [showTooltip],
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isHoveringMarkerRef.current) return;

    const marker = findMarker(e.target);
    if (!marker) return;

    setTooltipState(prev => ({
      ...prev,
      position: calculatePosition(e.clientX, e.clientY),
    }));
  }, []);

  const handleMouseOut = useCallback(
    (e: MouseEvent) => {
      const leaving = findMarker(e.target);
      const entering = findMarker(e.relatedTarget);

      if (leaving && leaving !== entering) {
        hideTooltip();
      }
    },
    [hideTooltip],
  );

  useEffect(() => {
    if (!enabled) {
      if (isHoveringMarkerRef.current) {
        isHoveringMarkerRef.current = false;
        enableChartTooltip();
      }
      setTooltipState({ data: null, position: null, visible: false });
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mouseover', handleMouseOver, true);
    container.addEventListener('mousemove', handleMouseMove, true);
    container.addEventListener('mouseout', handleMouseOut, true);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver, true);
      container.removeEventListener('mousemove', handleMouseMove, true);
      container.removeEventListener('mouseout', handleMouseOut, true);

      if (isHoveringMarkerRef.current) {
        isHoveringMarkerRef.current = false;
        enableChartTooltip();
      }
    };
  }, [enabled, containerRef, handleMouseOver, handleMouseMove, handleMouseOut, enableChartTooltip]);

  return { tooltipState };
};

export default useEngineStateTooltip;
